import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  ChevronUpIcon,
  ChevronDownIcon,
  Cog6ToothIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import DropdownOptionsService from '../services/dropdownOptionsService';

const DropdownOptionsManager = () => {
  const [options, setOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('status');
  const [editingOption, setEditingOption] = useState(null);
  const [newOption, setNewOption] = useState({ label: '', value: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [notification, setNotification] = useState(null);

  const categories = {
    status: { label: 'Lead Status', icon: '📈' }
  };

  useEffect(() => {
    loadOptions();
  }, []);

  const loadOptions = async () => {
    setLoading(true);
    const result = await DropdownOptionsService.getAllOptions();
    if (result.success) {
      setOptions(result.data);
    } else {
      showNotification('Failed to load options: ' + result.error, 'error');
    }
    setLoading(false);
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const handleAddOption = async () => {
    if (!newOption.label.trim() || !newOption.value.trim()) {
      showNotification('Please fill in both label and value', 'error');
      return;
    }

    const result = await DropdownOptionsService.createOption({
      category: selectedCategory,
      label: newOption.label.trim(),
      value: newOption.value.trim(),
      sortOrder: (options[selectedCategory]?.length || 0) + 1
    });

    if (result.success) {
      showNotification('Option added successfully!');
      setNewOption({ label: '', value: '' });
      setShowAddForm(false);
      loadOptions();
    } else {
      showNotification('Failed to add option: ' + result.error, 'error');
    }
  };

  const handleUpdateOption = async (id, updatedData) => {
    const result = await DropdownOptionsService.updateOption(id, updatedData);
    if (result.success) {
      showNotification('Option updated successfully!');
      setEditingOption(null);
      loadOptions();
    } else {
      showNotification('Failed to update option: ' + result.error, 'error');
    }
  };

  const handleDeleteOption = async (id) => {
    if (!window.confirm('Are you sure you want to delete this option?')) {
      return;
    }

    const result = await DropdownOptionsService.deleteOption(id);
    if (result.success) {
      showNotification('Option deleted successfully!');
      loadOptions();
    } else {
      showNotification('Failed to delete option: ' + result.error, 'error');
    }
  };

  const handleMoveOption = async (optionId, direction) => {
    const categoryOptions = options[selectedCategory] || [];
    const currentIndex = categoryOptions.findIndex(opt => opt._id === optionId);
    
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= categoryOptions.length) return;

    // Swap sort orders
    const updatedOptions = [...categoryOptions];
    const temp = updatedOptions[currentIndex].sortOrder;
    updatedOptions[currentIndex].sortOrder = updatedOptions[newIndex].sortOrder;
    updatedOptions[newIndex].sortOrder = temp;

    // Update backend
    const sortUpdates = [
      { id: updatedOptions[currentIndex]._id, sortOrder: updatedOptions[currentIndex].sortOrder },
      { id: updatedOptions[newIndex]._id, sortOrder: updatedOptions[newIndex].sortOrder }
    ];

    const result = await DropdownOptionsService.updateSortOrder(sortUpdates);
    if (result.success) {
      loadOptions();
    } else {
      showNotification('Failed to reorder options: ' + result.error, 'error');
    }
  };

  const initializeDefaults = async () => {
    if (!window.confirm('This will initialize default options. Continue?')) {
      return;
    }

    const result = await DropdownOptionsService.initializeDefaultOptions();
    if (result.success) {
      showNotification('Default options initialized successfully!');
      loadOptions();
    } else {
      showNotification('Failed to initialize: ' + result.error, 'error');
    }
  };

  const currentOptions = options[selectedCategory] || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
            <Cog6ToothIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Dropdown Options Manager</h2>
            <p className="text-sm text-gray-600">Manage dropdown options for lead forms</p>
          </div>
        </div>
        <button
          onClick={initializeDefaults}
          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200 text-sm"
        >
          Initialize Defaults
        </button>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`mb-4 p-4 rounded-lg flex items-center space-x-2 ${
          notification.type === 'error' 
            ? 'bg-red-50 text-red-800 border border-red-200' 
            : 'bg-green-50 text-green-800 border border-green-200'
        }`}>
          {notification.type === 'error' ? (
            <ExclamationTriangleIcon className="h-5 w-5" />
          ) : (
            <CheckIcon className="h-5 w-5" />
          )}
          <span>{notification.message}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Category Selector */}
        <div className="lg:col-span-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Categories</h3>
          <div className="space-y-2">
            {Object.entries(categories).map(([key, category]) => (
              <button
                key={key}
                onClick={() => {
                  setSelectedCategory(key);
                  setShowAddForm(false);
                  setEditingOption(null);
                }}
                className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 flex items-center space-x-3 ${
                  selectedCategory === key
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md'
                    : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                }`}
              >
                <span className="text-lg">{category.icon}</span>
                <div>
                  <div className="font-medium">{category.label}</div>
                  <div className={`text-sm ${selectedCategory === key ? 'text-blue-100' : 'text-gray-500'}`}>
                    {(options[key] || []).length} options
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Options List */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {categories[selectedCategory]?.label} Options
            </h3>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Option
            </button>
          </div>

          {/* Add New Option Form */}
          {showAddForm && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
              <h4 className="font-medium text-gray-900 mb-3">Add New Option</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Label (display text)"
                  value={newOption.label}
                  onChange={(e) => setNewOption({ ...newOption, label: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="text"
                  placeholder="Value (internal value)"
                  value={newOption.value}
                  onChange={(e) => setNewOption({ ...newOption, value: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="flex justify-end space-x-2 mt-3">
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewOption({ label: '', value: '' });
                  }}
                  className="px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddOption}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Add Option
                </button>
              </div>
            </div>
          )}

          {/* Options List */}
          <div className="space-y-2">
            {currentOptions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Cog6ToothIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No options found for this category</p>
                <p className="text-sm">Click "Add Option" to create the first one</p>
              </div>
            ) : (
              currentOptions.map((option, index) => (
                <div
                  key={option._id}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200"
                >
                  {editingOption === option._id ? (
                    <EditOptionForm
                      option={option}
                      onSave={(data) => handleUpdateOption(option._id, data)}
                      onCancel={() => setEditingOption(null)}
                    />
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{option.label}</div>
                        <div className="text-sm text-gray-500">Value: {option.value}</div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {/* Move Up/Down */}
                        <button
                          onClick={() => handleMoveOption(option._id, 'up')}
                          disabled={index === 0}
                          className={`p-1 rounded ${
                            index === 0 
                              ? 'text-gray-300 cursor-not-allowed' 
                              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <ChevronUpIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleMoveOption(option._id, 'down')}
                          disabled={index === currentOptions.length - 1}
                          className={`p-1 rounded ${
                            index === currentOptions.length - 1 
                              ? 'text-gray-300 cursor-not-allowed' 
                              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <ChevronDownIcon className="h-4 w-4" />
                        </button>
                        
                        {/* Edit */}
                        <button
                          onClick={() => setEditingOption(option._id)}
                          className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        
                        {/* Delete */}
                        <button
                          onClick={() => handleDeleteOption(option._id)}
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Edit Option Form Component
const EditOptionForm = ({ option, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    label: option.label,
    value: option.value
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.label.trim() || !formData.value.trim()) {
      alert('Please fill in both label and value');
      return;
    }
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input
          type="text"
          placeholder="Label"
          value={formData.label}
          onChange={(e) => setFormData({ ...formData, label: e.target.value })}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          required
        />
        <input
          type="text"
          placeholder="Value"
          value={formData.value}
          onChange={(e) => setFormData({ ...formData, value: e.target.value })}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          required
        />
      </div>
      <div className="flex justify-end space-x-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <CheckIcon className="h-4 w-4" />
        </button>
      </div>
    </form>
  );
};

export default DropdownOptionsManager;
